<!-- src/lib/components/EmptyState.svelte -->
<!--
  Shared empty-state component. Renders a centered illustration glyph
  + heading + optional subtitle. Replaces the ad-hoc "No X" text
  scattered across views.

  Refinement #46 from the 50-refinements spec.

  Usage:
    <EmptyState
      icon="board"
      title="No board selected"
      subtitle="Create a workspace and project to get started."
    />

  Available icons:
    inbox | board | timeline | agents | chat | design | drive |
    folder | search | settings | generic
-->
<script lang="ts">
  type IconName = 'inbox' | 'board' | 'timeline' | 'agents' | 'chat' |
    'design' | 'drive' | 'folder' | 'search' | 'settings' | 'generic';

  interface Props {
    icon?:     IconName;
    title:     string;
    subtitle?: string;
    ctaLabel?: string;
    onCta?:    () => void;
  }
  let { icon = 'generic', title, subtitle, ctaLabel, onCta }: Props = $props();
</script>

<div class="empty-state">
  <div class="empty-glyph">
    {#if icon === 'board'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="6" y="8" width="44" height="40" rx="4"/>
        <line x1="22" y1="8" x2="22" y2="48"/>
        <line x1="38" y1="8" x2="38" y2="48"/>
        <rect x="10" y="14" width="8" height="6" rx="1" fill="currentColor" opacity="0.15"/>
        <rect x="26" y="14" width="8" height="10" rx="1" fill="currentColor" opacity="0.15"/>
        <rect x="42" y="14" width="8" height="4" rx="1" fill="currentColor" opacity="0.15"/>
      </svg>
    {:else if icon === 'inbox'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 32l8-20h24l8 20"/>
        <path d="M8 32v12a4 4 0 004 4h32a4 4 0 004-4V32"/>
        <path d="M8 32h12a2 2 0 012 2v2a2 2 0 002 2h8a2 2 0 002-2v-2a2 2 0 012-2h12"/>
      </svg>
    {:else if icon === 'timeline'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <circle cx="14" cy="20" r="4"/>
        <circle cx="28" cy="28" r="4"/>
        <circle cx="42" cy="16" r="4"/>
        <line x1="18" y1="20" x2="24" y2="28"/>
        <line x1="32" y1="28" x2="38" y2="16"/>
        <line x1="8" y1="44" x2="48" y2="44" stroke-dasharray="3 3" opacity="0.3"/>
      </svg>
    {:else if icon === 'agents'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="12" y="16" width="32" height="24" rx="4"/>
        <line x1="20" y1="10" x2="20" y2="16"/>
        <line x1="36" y1="10" x2="36" y2="16"/>
        <line x1="12" y1="26" x2="44" y2="26"/>
        <circle cx="23" cy="33" r="2" fill="currentColor" opacity="0.3"/>
        <circle cx="33" cy="33" r="2" fill="currentColor" opacity="0.3"/>
      </svg>
    {:else if icon === 'chat'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M48 36a4 4 0 01-4 4H16l-8 8V12a4 4 0 014-4h32a4 4 0 014 4z"/>
        <line x1="16" y1="20" x2="40" y2="20" opacity="0.3"/>
        <line x1="16" y1="28" x2="32" y2="28" opacity="0.3"/>
      </svg>
    {:else if icon === 'design'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M28 40l14-14 6 6-14 14-6-6z"/>
        <path d="M36 26l-3-14L8 8l7 25 14 3 7-10z"/>
        <circle cx="20" cy="24" r="3" fill="currentColor" opacity="0.15"/>
      </svg>
    {:else if icon === 'drive'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M40 22h-2.5A16 16 0 1018 42h22a10 10 0 000-20z"/>
      </svg>
    {:else if icon === 'folder'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 16a4 4 0 014-4h12l4 4h16a4 4 0 014 4v24a4 4 0 01-4 4H12a4 4 0 01-4-4z"/>
      </svg>
    {:else if icon === 'search'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <circle cx="24" cy="24" r="14"/>
        <line x1="34" y1="34" x2="46" y2="46"/>
      </svg>
    {:else if icon === 'settings'}
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="28" cy="28" r="8"/>
        <path d="M28 8v6M28 42v6M8 28h6M42 28h6M14 14l4 4M38 38l4 4M14 42l4-4M38 18l4-4"/>
      </svg>
    {:else}
      <!-- generic empty glyph -->
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="8" y="12" width="40" height="32" rx="4"/>
        <line x1="18" y1="24" x2="38" y2="24" opacity="0.3"/>
        <line x1="18" y1="32" x2="30" y2="32" opacity="0.3"/>
      </svg>
    {/if}
  </div>
  <p class="empty-title">{title}</p>
  {#if subtitle}
    <p class="empty-subtitle">{subtitle}</p>
  {/if}
  {#if ctaLabel && onCta}
    <button class="empty-cta" onclick={onCta}>{ctaLabel}</button>
  {/if}
</div>

<style>
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
    color: oklch(0.55 0.04 310 / 0.55);
    gap: 0.75rem;
    flex: 1;
    min-height: 200px;
  }
  .empty-glyph {
    color: oklch(0.50 0.06 310 / 0.30);
    margin-bottom: 0.5rem;
  }
  .empty-title {
    margin: 0;
    font-size: 0.92rem;
    font-weight: 500;
    color: oklch(0.72 0.05 310 / 0.70);
  }
  .empty-subtitle {
    margin: 0;
    font-size: 0.78rem;
    color: oklch(0.55 0.04 310 / 0.50);
    max-width: 38ch;
    line-height: 1.5;
  }
  .empty-cta {
    margin-top: 0.5rem;
    padding: 0.5rem 1.25rem;
    background: oklch(0.72 0.28 310 / 0.15);
    border: 1px solid oklch(0.72 0.28 310 / 0.35);
    color: oklch(0.88 0.12 310);
    border-radius: 6px;
    font-size: 0.82rem;
    cursor: pointer;
    font-family: inherit;
    transition: background 100ms ease, border-color 100ms ease;
  }
  .empty-cta:hover {
    background: oklch(0.72 0.28 310 / 0.25);
    border-color: oklch(0.72 0.28 310 / 0.55);
  }
</style>
